import * as PopoverPrimitive from '@radix-ui/react-popover';
import classNames from 'classnames';
import React, { type ReactNode, useMemo } from 'react';
import { useIsMounted } from '~/hooks/useIsMounted';

interface Props {
  trigger: React.ReactNode;
  children: (closePopover: () => void) => ReactNode;
  container?: HTMLDivElement | null;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  usePortal?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function Popover({
  trigger,
  children,
  container,
  side = 'top',
  align = 'start',
  sideOffset,
  onOpenChange,
  usePortal = false,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const isMounted = useIsMounted();

  const closePopover = () => {
    setTimeout(() => {
      if (isMounted()) {
        setOpen(false);
      }
    }, 0);
  };

  const content = useMemo(() => {
    const _content = (
      <PopoverPrimitive.Content sideOffset={sideOffset} side={side} align={align}>
        {children(closePopover)}
      </PopoverPrimitive.Content>
    );
    if (usePortal) {
      return <PopoverPrimitive.Portal container={container}>{_content}</PopoverPrimitive.Portal>;
    } else {
      return _content;
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  }, [usePortal, children, closePopover, sideOffset, side, align, container]);

  return (
    <PopoverPrimitive.Root
      //modal={true}
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        onOpenChange?.(isOpen);
      }}
    >
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      {content}
    </PopoverPrimitive.Root>
  );
}

interface PopoverContentProps {
  children?: React.ReactNode;
  className?: string;
}

export const PopoverContent = ({ children, className }: PopoverContentProps) => {
  return <div className={classNames('rounded-sm border bg-white p-2 text-sm', className)}>{children}</div>;
};
